/**
 * A person as a circle: their picture when there is one, otherwise two letters. The letters come
 * from the name, or from the start of the email when there is no name, and every avatar is the same
 * colour so no one is ranked by it. A picture that fails to load falls back to the letters.
 *
 * It is decoration. The name or email beside it says who it is, so it is hidden from assistive
 * technology.
 */
import * as stylex from "@stylexjs/stylex";
import { useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
const styles = {
  avatar: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmuXW: "x2lah0s",
    kVQacm: "xb3r6kr",
    kaIpWk: "x1m3fsnl",
    kWkggS: "xmbr0q7",
    kMwMTN: "xxil687",
    k63SB2: "xqu410z",
    kLWn49: "x1e5flnj",
    $$css: true
  },
  sm: {
    kzqmXN: "x1urbi3r",
    kZKoxP: "x18fbomr",
    kGuDYH: "xl4dl4e",
    $$css: true
  },
  lg: {
    kzqmXN: "x15mio10",
    kZKoxP: "x2y5be4",
    kGuDYH: "x1rf3zrh",
    $$css: true
  },
  picture: {
    kzqmXN: "xh8yej3",
    kZKoxP: "x5yr21d",
    kVIFPx: "xl1xv1r",
    $$css: true
  }
};

/** Two letters from a name, or from the email when there is no name, or "?" when there is neither. */
export function initialsOf(name: string | null, email: string | null): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(part => part !== "");
  const first = parts[0]?.charAt(0) ?? "";
  const last = parts.length > 1 ? parts.at(-1)?.charAt(0) ?? "" : "";
  const letters = `${first}${last}`.toUpperCase();
  if (letters !== "") return letters;
  const local = (email ?? "").split("@")[0] ?? "";
  const fromEmail = local.replaceAll(/[^a-zA-Z0-9]/g, "").slice(0, 2).toUpperCase();
  return fromEmail === "" ? "?" : fromEmail;
}
export function Avatar({
  name,
  email,
  src,
  size = "sm",
  testId
}: {
  readonly name: string | null;
  readonly email: string | null;
  readonly src?: string;
  readonly size?: "sm" | "lg";
  readonly testId?: string;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  const showPicture = src !== undefined && src !== failed;
  return <span aria-hidden="true" data-testid={testId} {...stylex.props(styles.avatar, styles[size])}>
      {showPicture ? <img src={src} alt="" onError={() => setFailed(src)} className="xh8yej3 x5yr21d xl1xv1r" /> : initialsOf(name, email)}
    </span>;
}